import type { JSX } from "react";
import { Button, Checkbox, Text, View } from "@/components";
import type { AsideFiltersProps } from "@/components/types";
import { useLayout } from "@/providers";

const AsideFilters = ({
  checkboxFilters,
  values,
  onChange,
  title,
  onReset,
  children
}: AsideFiltersProps): JSX.Element => {
  const { t } = useLayout();

  const handleCheckboxChange = (
    checkboxSectionID: string,
    checkboxId: string,
    checked: boolean
  ) => {
    const current = values[checkboxSectionID] || [];
    const updated = checked
      ? [...current, checkboxId]
      : current.filter((id) => id !== checkboxId);

    const newValues = { ...values, [checkboxSectionID]: updated };

    onChange(newValues);
  };

  return (
    <View
      type="aside"
      aria-label={t("filters")}
      className="w-full xl:w-1/3 bg-card text-card-foreground rounded-2xl p-6 border border-border h-fit"
    >
      {title && (
        <>
          <View className="flex items-center justify-between gap-4 mb-6">
            <Text type="h2" className="text-xl font-bold text-card-foreground">
              {title}
            </Text>
            {onReset && (
              <Button
                onClick={onReset}
                className="px-4 py-1.5 bg-primary text-primary-foreground text-xs font-medium rounded-full hover:opacity-90 transition-colors"
              >
                {t("resetFilters")}
              </Button>
            )}
          </View>
          <View className="w-full h-px bg-border mb-6" />
        </>
      )}
      {checkboxFilters.map((section, index) => (
        <View key={section.id}>
          {index > 0 && <View className="w-full h-px bg-border my-6" />}
          <View className="mb-8 last:mb-0">
            <Text
              type="h5"
              className="text-base font-bold text-card-foreground mb-4"
            >
              {section.title}
            </Text>
            <View className="flex flex-col gap-3">
              {section.items?.map((item) => (
                <View key={item.id}>
                  <Checkbox
                    name={item.id}
                    label={item.label}
                    onChange={({ target: { checked } }) =>
                      handleCheckboxChange(section.id, item.id, checked)
                    }
                  />
                </View>
              ))}
            </View>
          </View>
        </View>
      ))}
      {children && (
        <>
          <View className="w-full h-px bg-border my-6" />
          {children}
        </>
      )}
    </View>
  );
};

export default AsideFilters;
